
import { CipherQuestion } from './cipherTypes';
import { LoadingState } from './LoadingState';
import QuizHeader from './components/QuizHeader';
import QuizQuestion from './components/QuizQuestion';
import QuizNavigation from './components/QuizNavigation';
import { useQuizCompletion } from './components/QuizCompletion';
import { useQuizState } from './components/QuizState';
import { useQuizLogic } from './components/QuizLogic';
import { AuthenticationStatus } from './components/AuthenticationStatus';

interface CipherQuizFormProps {
  questions: CipherQuestion[];
  hasToken: boolean;
  walletAddress: `0x${string}`;
  onComplete: (rank: string, brocode: string) => void;
  tableReady: boolean;
}

const CipherQuizForm = ({ 
  questions, 
  hasToken, 
  walletAddress, 
  onComplete, 
  tableReady = true 
}: CipherQuizFormProps) => {
  const {
    currentQuestionIndex,
    setCurrentQuestionIndex,
    selectedOption,
    setSelectedOption,
    selectedOptions,
    setSelectedOptions,
    correctAnswers,
    setCorrectAnswers,
    isSubmitting,
    setIsSubmitting,
    resetQuiz
  } = useQuizState();

  const { isMultiSelectQuestion, checkAnswer } = useQuizLogic();

  const handleOptionSelect = (option: string) => {
    if (isMultiSelectQuestion(currentQuestionIndex)) {
      // For multi-select questions, toggle the selection
      setSelectedOptions(prev => {
        if (prev.includes(option)) {
          return prev.filter(item => item !== option);
        } else {
          return [...prev, option];
        }
      });
    } else {
      // For single-select questions
      setSelectedOption(option);
    }
  };

  const { handleQuizCompletion } = useQuizCompletion({
    questions,
    selectedOptions,
    selectedOption,
    correctAnswers,
    currentQuestionIndex,
    walletAddress,
    hasToken,
    onComplete,
    checkAnswer: () => {
      const question = questions[currentQuestionIndex];
      return question ? checkAnswer(question, currentQuestionIndex, selectedOption, selectedOptions) : false;
    }
  });

  const handleNextQuestion = async () => {
    if (isMultiSelectQuestion(currentQuestionIndex) && selectedOptions.length === 0) return;
    if (!isMultiSelectQuestion(currentQuestionIndex) && !selectedOption) return;

    const question = questions[currentQuestionIndex];
    if (!question) return;
    const isCorrect = checkAnswer(question, currentQuestionIndex, selectedOption, selectedOptions);
    
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setSelectedOptions([]);
    } else {
      // Quiz completed
      setIsSubmitting(true);
      const result = await handleQuizCompletion();
      if (!result.success) {
        // Reset quiz
        resetQuiz();
      }
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isMultiSelect = isMultiSelectQuestion(currentQuestionIndex);
  const requiredSelections = isMultiSelect ? 3 : 1;

  if (isSubmitting) {
    return <LoadingState message="Verifying your answers..." />;
  }

  return (
    <div className="max-w-2xl mx-auto bg-navy-light p-8 rounded-lg border border-tan-dark">
      <AuthenticationStatus />
      
      <QuizHeader 
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={questions.length}
      />
      
      {currentQuestion && (
        <QuizQuestion
          question={currentQuestion}
        selectedOption={selectedOption}
        selectedOptions={selectedOptions}
        isMultiSelect={isMultiSelect}
        requiredSelections={requiredSelections}
        onOptionSelect={handleOptionSelect}
        />
      )}
      
      <QuizNavigation
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={questions.length}
        isMultiSelect={isMultiSelect}
        selectedOption={selectedOption}
        selectedOptions={selectedOptions}
        requiredSelections={requiredSelections}
        tableReady={tableReady}
        onNextQuestion={handleNextQuestion}
      />
    </div>
  );
};

export default CipherQuizForm;
